import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Grid,
    Link,
    Tab,
    Tabs,
    Theme,
    Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import useReactRouter from 'use-react-router';
import { LoadingOverlay } from '../../../components/LoadingOverlay/LoadingOverlay';
import { Page } from '../../../components/Page';
import PageHeader from '../../../components/PageHeader';
import { GroupBooking } from '../../../graphql/groups.datatypes';
import { getBillingParty } from '../getBillingParty.api';
import BillingPartyEdit from './BillingPartyEdit';
import getGroupBooking from '../../GroupBookings/getGroupBooking.api';
import { WcvType } from '../../../graphql/logs.datatypes';
import AddNote from '../../Logs/AddNote/AddNote';
import { BillingPartyMembersList } from '../BillingPartyMembersList';
import SendEmailToGroup from '../../../components/SendEmailToGroup';
import PaymentScheduleDetailsModal from '../../PaymentSchedule/PaymentScheduleDetailsModal';
import { BillingParty } from '../../../graphql/billing_party.datatypes';
import { BillingPartyPaymentsList } from '../BillingPartyPaymentsList';
import { LogListContent } from '../../Logs/LogList/LogList';
import { getHost } from '../../../graphql/GraphQlProvider';
import { BillingPartyAdjustmentsListContent } from '../BillingPartyAdjustmentsList/BillingPartyAdjustmentsList';
import { Can } from '../../../components/Can';
import moment from 'moment';
import { getMe } from '../../../graphql/auth';
import { checkIsAdmin } from '../../../utils/checkIsAdmin';
import { formatMoney } from '../../../utils/formatters';
import { NotesList } from '../../Notes';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(3)
    },
    tabs: {
        marginTop: theme.spacing(3)
    },
    container: {
        marginTop: theme.spacing(3),
        width: '100%',
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down('md')]: {
            flexWrap: 'wrap'
        }
    },
    card: {
        [theme.breakpoints.down('md')]: {

            flexWrap: 'wrap',
        }
    },
    linkButton: {
        marginRight: theme.spacing(1),
        [theme.breakpoints.down('md')]: {
            marginBottom: 10,
        }
    },
    noteButton: {
        marginTop: 10,
    },
    buttonWrapper: {
        [theme.breakpoints.down('md')]: {

            flexWrap: 'wrap',
            marginBottom: 10,
        },
    },
    buttonHeader: {
        [theme.breakpoints.down('md')]: {
            flex: 'auto',
            marginTop: 10,
            marginRight: 0,
            flexWrap: 'wrap',
        }
    },
    editLink: {
        textDecoration: 'underline',
    },
    editRoom: {
        display: 'flex',
    },
}));

const getBlankBillingParty = (groupBooking: GroupBooking | null): BillingParty => {
    return {
        __typename: 'BillingParty',
        id: -1,
        group_booking_id: groupBooking ? groupBooking.id : -1,
        payment_schedule_id: null,
        credit_card_fee: groupBooking ? groupBooking.group!.credit_card_fee ? groupBooking.group!.credit_card_fee : 0 : 0,
        soft_cancelled: false,
        disabled: false,
        signature: '',
        comp_credit_eligible: false,

        members_count: 0,
        payments_count: 0,
        performers_count: 0,

        payingMembers: [],
        booking: groupBooking ? groupBooking : undefined,
        members: [],
        hasPendingECheckPayments: false
    };
};

export const BillingPartyDetailsContent = (props: { billingParty: BillingParty, isAdmin: boolean, canEdit: boolean, groupRef?: string }) => {
    const {billingParty, isAdmin, groupRef, canEdit} = props;
    const classes = useStyles();
    const {history} = useReactRouter();
    const newBillingParty = billingParty.id === -1;
    const [tab, setTab] = useState(newBillingParty || !isAdmin ? 'details' : 'notes');
    const [openModal, setOpenModal] = useState(false);

    const [openContactModal, setOpenContactModal] = React.useState({message: '', open: false});
    const handleContactModalOpen = () => setOpenContactModal({
        message: ``,
        open: true
    });
    const handleContactModalClose = () => setOpenContactModal({message: '', open: false});

    const [openPaymentScheduleModal, setOpenPaymentScheduleModal] = React.useState(false);
    const handlePaymentScheduleModalOpen = () => setOpenPaymentScheduleModal(true);
    const handlePaymentScheduleModalClose = () => setOpenPaymentScheduleModal(false);

    const showPrices = isAdmin || (moment(billingParty.booking!.end_date).add(15, 'days').diff(moment(), 'days') > 0);

    const meData = getMe();
    const checkEmail = meData && meData.data && meData.data.me && meData.data.me.email;
    const payingMemberLoggedIn = billingParty && billingParty.payingMembers && billingParty.payingMembers.find(member => checkEmail && member.email === checkEmail);
    const canEditDetails = canEdit || !!payingMemberLoggedIn;

    return billingParty && (
        <div>
            {!newBillingParty && (
                <Card className={classes.container}>
                    <CardHeader
                        title={'Party Totals'}
                        className={classes.buttonWrapper}
                        classes={{action: classes.buttonHeader}}
                        action={[
                            isAdmin && (
                                <Button key={1} variant="contained" className={classes.linkButton}
                                                onClick={() => history.push('/groups/' + billingParty.booking!.group!.id)}>
                                    Back to Group
                                </Button>),

                            !isAdmin && (
                                <Can key={1} checkAll={false}
                                     permission={['groups.read', 'associated.groups.read']}>
                                    <Button
                                        variant="contained"
                                        className={classes.linkButton}
                                        onClick={() => history.push('/mygroups/' + billingParty.booking!.group!.booking_form_url)}>
                                        Back to Group
                                    </Button>
                                </Can>),

                            isAdmin && (
                                <Button key={2} variant="contained" className={classes.linkButton} onClick={() => handleContactModalOpen()}>
                                    Email Party
                                </Button>),

                            isAdmin && (
                                <Button key={3} variant="contained" className={classes.linkButton}
                                        onClick={() => history.push('/admin_booking/' + billingParty.booking!.group!.booking_form_url + '/' + billingParty.booking!.id)}>
                                    Load In Booking Tool
                                </Button>),

                            isAdmin && billingParty.payment_schedule_id && (
                                <Button key={5} variant="contained" className={classes.linkButton}
                                    onClick={() => handlePaymentScheduleModalOpen()}>
                                    Payment Schedule
                                </Button>),

                            isAdmin && showPrices &&
                            <Link key={6}
                                  href={getHost() + '/invoice/' + billingParty.code}>
                                <Button variant="contained">Download Invoice</Button>
                            </Link>,
                        ]}
                    />
                    <Divider/>
                    {showPrices && (
                        <CardContent className={classes.card}>
                            <Grid container alignItems="center" spacing={1}>
                                <Grid item lg={3} md={4} xs={12}>
                                    <Typography variant="h3">Grand Total: {formatMoney(billingParty.cost!, true)}</Typography>
                                </Grid>
                                <Grid item lg={3} md={4} xs={12}>
                                    <Typography variant="h3">Processing Fees: {formatMoney(billingParty.totalCreditCardFees!, true) }</Typography>
                                </Grid>
                                <Grid item lg={3} md={4} xs={12}>
                                    <Typography variant="h3">Outstanding: {formatMoney(billingParty.outstandingBalance!, true)}</Typography>
                                </Grid>
                                <Grid item lg={3} md={4} xs={12}>
                                    <Typography variant="h3">Past due: {formatMoney(billingParty.overduePayments!, true)}</Typography>
                                </Grid>

                                {!!billingParty.totalReceived && billingParty.totalReceived > 0 && (
                                    <Grid item lg={4} md={4} xs={12}>
                                        <Typography
                                            variant="h3">Received: {formatMoney(billingParty.totalReceived!, true)}</Typography>
                                    </Grid>
                                )}
                                {!!billingParty.totalRefunds && billingParty.totalRefunds > 0 && (
                                    <Grid item lg={4} md={4} xs={12}>
                                        <Typography
                                            variant="h3">Refunded: {formatMoney(billingParty.totalRefunds!, true)}</Typography>
                                    </Grid>
                                )}
                                {!!billingParty.totalCredit && billingParty.totalCredit > 0 && (
                                    <Grid item lg={4} md={4} xs={12}>
                                        <Typography
                                            variant="h3">Credit: {formatMoney(billingParty.totalCredit!, true)}</Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    )}
                </Card>
            )}

            <Tabs className={classes.tabs} onChange={(event, value) => setTab(value)} scrollButtons="auto"
                  value={tab}
                  variant="scrollable">
                {!newBillingParty && isAdmin && <Tab label="Notes" value="notes"/>}
                <Tab label="Details" value="details"/>
                {!newBillingParty && <Tab label="Group Members" value="group_members"/>}
                {!newBillingParty && showPrices && <Tab label="Payments" value="payments"/>}
                {!newBillingParty && showPrices && <Tab label="Adjustments" value="adjustments"/>}
                {!newBillingParty && isAdmin && <Tab label="Logs" value="logs"/>}
            </Tabs>

            {tab === 'details' && (
                <BillingPartyEdit billingParty={billingParty} isAdmin={isAdmin} canEdit={canEditDetails}/>
            )}

            {tab === 'group_members' && (
                <BillingPartyMembersList billingParty={billingParty} isAdmin={isAdmin} canEdit={canEdit}
                                         groupRef={groupRef}/>
            )}

            {tab === 'payments' && (
                <BillingPartyPaymentsList billingParty={billingParty} isAdmin={isAdmin} canEdit={canEdit}
                                          groupRef={groupRef}/>
            )}

            {tab === 'adjustments' && (
                <BillingPartyAdjustmentsListContent billingParty={billingParty} isAdmin={isAdmin} canEdit={canEdit}/>
            )}

            {tab === 'notes' && (
                <div>
                    <Button
                        color="primary"
                        onClick={() => setOpenModal(true)}
                        size="small"
                        variant="outlined"
                        className={classes.noteButton}
                    >
                        Add Note
                    </Button>
                    <NotesList type={'billing_party'} type_id={billingParty.id} />
                    {openModal && (<AddNote onClose={() => setOpenModal(false)} type={WcvType.BILLING_PARTY}
                                            type_id={billingParty.id}/>)}
                </div>
            )}

            {tab === 'logs' && <LogListContent type={[WcvType.BILLING_PARTY, WcvType.PAYMENT]} type_id={billingParty.id}/>}

            <SendEmailToGroup onClose={handleContactModalClose} open={openContactModal.open}
                              fromEmail={billingParty.booking && billingParty.booking.group && billingParty.booking.group.tour && billingParty.booking.group.tour.comms_email_address}
                              billingPartyId={billingParty.id}
                              softCancelled={billingParty.soft_cancelled}
                              heading={'Send Message to Billing Party'}
                              message={'This message will be sent to ever paying member in this billing party.'}
            />

            {billingParty.payment_schedule_id && (
                <PaymentScheduleDetailsModal onClose={handlePaymentScheduleModalClose}
                                             open={openPaymentScheduleModal}
                                             paymentScheduleId={billingParty.payment_schedule_id}/>
            )}

        </div>);
};

export const BillingPartyDetailsLoad = (props: { id: number, bookingId?: string, isAdmin: boolean, canEdit: boolean, groupRef?: string }) => {
    const {enqueueSnackbar} = useSnackbar();
    const {history} = useReactRouter();
    const classes = useStyles();
    const {id, bookingId, isAdmin, canEdit, groupRef} = props;
    const newBillingParty = bookingId !== undefined;
    const bookingData = bookingId ? getGroupBooking(Number(bookingId)) : {loading: false, data: null};
    const groupBooking = bookingData && bookingData.data && bookingData.data.groupBooking ? bookingData.data.groupBooking : null;

    const billingPartyData = newBillingParty ? {
        loading: false,
        data: {billingParty: getBlankBillingParty(groupBooking)},
        error: null
    } : getBillingParty(id);

    useEffect(() => {
        if (billingPartyData.error) {
            enqueueSnackbar(`There was an error loading Billing Party ${id}`, {variant: 'error'});
            history.push('/billing_party');
        }
    }, [billingPartyData.error]);

    const billingParty = billingPartyData.data && billingPartyData.data.billingParty;
    const primary = billingParty && billingParty.payingMembers && billingParty.payingMembers.find(member => member.primary_paying_member);
    const title = 'Billing Party Details' + (primary ? ': ' + primary.first_name + ' ' + primary.last_name + ' (' + primary.email + ')' : '');
    const groupName = billingParty && billingParty.group ? billingParty.group.name : '';
    const roomParties: Record<number | string, string[]> = {};

    if (billingParty && billingParty.members) {
        billingParty.members.map(member => {
            const roomPartyId = member && member.roomParty && typeof member.roomParty.id === 'number' ? member && member.roomParty && member.roomParty.id : null;
            const memberName = member && member.groupMember ? `${member.groupMember.first_name} ${member.groupMember.last_name}` : ''

            if (roomPartyId !== null) {
                if (!roomParties[roomPartyId]) {
                    roomParties[roomPartyId] = [memberName];
                } else {
                    roomParties[roomPartyId].push(memberName);
                }
            }
        })
    }

    const roomPartyList = Object.entries(roomParties).map(([roomPartyId, memberNames]) => {
        return <div className={classes.editRoom}><Link key={roomPartyId} href={'/room_party/' + roomPartyId}><Typography variant="body2" color={'primary'} className={classes.editLink}>Room Party - {roomPartyId} - {memberNames.join(", ")}</Typography></Link></div>
    });

    return (
        <Page className={classes.root} title={title}>
            <PageHeader section={`groups ${groupName !== undefined ? groupName : ''}`} title={title} />
            {roomPartyList.map(party => {
                return party
            })}
            <LoadingOverlay loading={billingPartyData.loading || bookingData.loading}>
                {billingParty && (
                    <BillingPartyDetailsContent billingParty={billingParty} isAdmin={isAdmin} canEdit={canEdit}
                                                groupRef={groupRef}/>)}
            </LoadingOverlay>
        </Page>
    );
};

export const BillingPartyInMyGroupDetails = () => {
    const {match}: RouteComponentProps<{ json: string }> = useReactRouter();
    const {json} = match.params;
    const jsonObj = JSON.parse(atob(json));

    const isAdmin = checkIsAdmin();
    //TODO: add in canEdit check here i its not just for an admin.

    return (<BillingPartyDetailsLoad id={jsonObj.id} isAdmin={isAdmin} canEdit={isAdmin} groupRef={jsonObj.groupRef}/>);
};

export const BillingPartyDetails = () => {
    const {match}: RouteComponentProps<{ id: string, booking_id?: string }> = useReactRouter();
    const {id, booking_id} = match.params;
    return (<BillingPartyDetailsLoad id={Number(id)} bookingId={booking_id} isAdmin={true} canEdit={true}/>);
};

export default BillingPartyDetails;
